import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FormikHelpers, useFormik } from 'formik';
import Card, {
	CardActions,
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import { priceFormat } from '../../helpers/helpers';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../components/bootstrap/Dropdown';
import Icon from '../../components/icon/Icon';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../components/bootstrap/OffCanvas';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import data from '../../common/data/dummyEventsData';
import PaginationButtons, { dataPagination, PER_COUNT } from '../../components/PaginationButtons';
import useSortableData from '../../hooks/useSortableData';
import useDarkMode from '../../hooks/useDarkMode';
import { addUsers, changePassword, deleteUser, editUsers, getUsers, userStatusChange } from '../../services/users';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../../components/bootstrap/Modal';
import 'react-toastify/dist/ReactToastify.css';
import SvgDeleteForever from '../../components/icon/material-icons/DeleteForever';
import Spinner from '../../components/bootstrap/Spinner';

interface ICommonUpcomingEventsProps {
	isFluid?: boolean;
}

interface FormValues {
	password: string;
}
const CommonUpcomingEvents: FC<ICommonUpcomingEventsProps> = ({ isFluid }) => {
	const { themeStatus, darkModeStatus } = useDarkMode();
	const [users, setUsers] = useState<any[]>([]); // State to store users
	const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(true); // Loading state for users
	const [apiError, setApiError] = useState<string | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(PER_COUNT['5']);
	const [searchKeyword, setSearchKeyword] = useState('');
	const [totalUsers, setTotalUsers] = useState(0);
	const { items, requestSort, getClassNamesFor } = useSortableData(data);
	const [upcomingEventsAddModal, setUpcomingEventsAddModal] = useState(false);
	const [totalData,setTotalData] = useState(0);
	const [upcomingEventsDeleteModal, setUpcomingEventsDeleteModal] = useState(false);
	const [userId, setUserId] = useState("")
	// Fetch users when the component mounts
	const fetchUsers = async () => {
		try {
			const body = {
				searchKeyword: searchKeyword,
				perPage: perPage,
				page: currentPage,
				// status: 1
			};

			const response = await getUsers(body);
			if (response.error) {
				setApiError(response.error);
			} else {
				setUsers(response.data);
				setTotalData(response.dataCount);
				setTotalUsers(response.total || 0); // Assuming the API returns total count
			}
		} catch (error) {
			setApiError('Failed to fetch users');
		} finally {
			setIsLoadingUsers(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, [currentPage, perPage, searchKeyword]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchKeyword(e.target.value);
		setCurrentPage(1); // Reset to first page when search changes
	};

	// BEGIN :: Upcoming Events
	const [upcomingEventsInfoOffcanvas, setUpcomingEventsInfoOffcanvas] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null); 

	const handleUpcomingDetails = (user: User) => {
		setSelectedUser(user); // Set the selected user
		setUpcomingEventsInfoOffcanvas(true); // Open the OffCanvas
	};

	const [editUser, setEditUser] = useState<User | null>(null); // To store the user being edited
	const [upcomingEventsEditOffcanvas, setUpcomingEventsEditOffcanvas] = useState(false);

	type User = {
		password: string;
		_id: string,
		fName: string,
		lName: string,
		status: number,
		email: string,
		createdAt: string
	};

	type HandleUpcomingEdit = (user: User) => void;

	const handleUpcomingEdit: HandleUpcomingEdit = (user) => {
		setUpcomingEventsEditOffcanvas((prev) => !prev); // Toggle offcanvas
		setEditUser(user); // Set the user object
	};

	const handleDelete = async () => {
		try {
			await deleteUser(userId);
			setUpcomingEventsDeleteModal(false)
			fetchUsers();
			toast.success('User deleted successfully')
		} catch (error: any) {
			toast.error(error)
		}
	};

	const formik = useFormik<FormValues>({
		initialValues: {
			password: '',
		},
		validationSchema: Yup.object({
			password: Yup.string()
				.min(6, 'Password must be at least 6 characters')
				.matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
				.matches(/[\W_]/, 'Password must contain at least one special character')
				.required('Password is required'),
		}),

		onSubmit: async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
			try {

				await changePassword(editUser?._id, values);
				fetchUsers();
				formikHelpers.resetForm();
				setUpcomingEventsEditOffcanvas(false);
			} catch (error: any) {
				toast.error(error)
			}
		},
	});

	const addFormik = useFormik({
		initialValues: {
			fName: '',
			lName: '',
			email: '',
			password: '',
		},
		validationSchema: Yup.object({
			fName: Yup.string().required('First Name is required'),
			lName: Yup.string().required('Last Name is required'),
			email: Yup.string().email('Invalid email format').required('Email is required'),
			password: Yup.string()
				.min(6, 'Password must be at least 6 characters')
				.required('Password is required')
				.matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
				.matches(/[!@#$%^&*]/, 'Password must contain at least one special character'),
		}),
		onSubmit: async (values, formikHelpers) => {
			try {
				// Replace with your API call to add the user
				await addUsers(values); // Implement addUser function as per your API
				toast.success('User added successfully!');
				setUpcomingEventsAddModal(false); // Close the modal on success
				formikHelpers.resetForm();
				fetchUsers();
			} catch (error) {
				toast.error('Failed to add user. Please try again.');
			}
		},
	});

	const handleStatusChange = async (id: string, status: number) => {
		const result = await userStatusChange(id, { status: status }); // Call your API function with id and status
		if (result.error) {
			toast.error(result.error);
		} else {
			toast.success('User status changed successfully!');
			fetchUsers();
		}
	};
	return (
		<>
		{isLoadingUsers && (
                <div
                    style={{
                        position: 'fixed', // Fixed position to cover the entire viewport
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)', // Black background with 50% opacity
                        display: 'flex', // Use flexbox to center the spinner
                        justifyContent: 'center', // Center horizontally
                        alignItems: 'center', // Center vertically
                        zIndex: 9999, // Ensure it appears above other content
                    }}
                >
                    <Spinner size="3rem" /> {/* Adjust size as needed */}
                </div>
            )}
			<Card stretch={isFluid}>
				<CardHeader >
					<div className='d-flex justify-content-between align-items-center w-100'>
						{/* Search Input */}
						<div className='col-md-4'>
							<Input
								placeholder='Search users...'
								value={searchKeyword}
								onChange={handleSearchChange}
								type='search'
							// icon='Search'
							/>
						</div>

						<Button
							color={darkModeStatus ? 'light' : 'dark'}
							isLight
							icon='Add'
							onClick={() => setUpcomingEventsAddModal(true)}
						>
							Add User
						</Button>
					</div>
				</CardHeader>
				<CardBody className='table-responsive' isScrollable={isFluid}>
					<table className='table table-modern'>
						<thead>
							<tr>
								<td aria-labelledby='Image' style={{ width: 60 }} />
								<th
									onClick={() => requestSort('date')}
									className='cursor-pointer text-decoration-underline'>
									Date / Time{' '}
									<Icon
										size='lg'
										className={getClassNamesFor('date')}
										icon='FilterList'
									/>
								</th>
								<th>Email</th>
								<th>Firstname</th>
								<th>Lastname</th>
								<th>Created Date</th>
								<th>Status</th>
								<td aria-labelledby='Actions' />
							</tr>
						</thead>
						<tbody>
							{users?.length > 0 && users?.map((item) => (
								<tr key={item._id}>
									<td>
										<Button
											isOutline={!darkModeStatus}
											color='dark'
											isLight={darkModeStatus}
											className={classNames({
												'border-light': !darkModeStatus,
											})}
											icon='Info'
											onClick={() => handleUpcomingDetails(item)}
											aria-label='Detailed information'
										/>
									</td>
									<td>
										<div className='d-flex align-items-center'>
											<span
												className={classNames(
													'badge',
													'border border-2',
													[`border-${themeStatus}`],
													'rounded-circle',
													'bg-success',
													'p-2 me-2',
													`bg-${item.status.color}`,
												)}>
												<span className='visually-hidden'>
													{item.fName}
												</span>
											</span>
											<span className='text-nowrap'>
												{dayjs(item.createdAt).format('MMM Do YYYY, h:mm a')}
											</span>
										</div>
									</td>
									<td>
										<div>
											{/* <div>{item?.customer?.name}</div> */}
											<div className='small text-muted'>
												{item?.email}
											</div>
										</div>
									</td>
									<td>
										{item.fName}
									</td>
									<td>
										{item.lName}
									</td>
									<td>
										{dayjs(item.createdAt).format('MMM Do YYYY, h:mm a')}
									</td>
									<td>
										<Dropdown>
											<DropdownToggle hasIcon={false}>
												<Button
													isLink
													color={item.status === 1 ? 'success' : 'danger'} // Current status color
													icon='Circle'
													className='text-nowrap'>
													{item.status === 1 ? 'Active' : 'Inactive'} {/* Current status name */}
												</Button>
											</DropdownToggle>
											<DropdownMenu>
												{/* Map over the statuses for Active and Inactive */}
												{[0, 1].map((status) => (
													<DropdownItem
														key={status}
														onClick={() => handleStatusChange(item._id, status)} // Call handler to change status
													>
														<div>
															<Icon
																icon='Circle'
																color={status === 1 ? 'success' : 'danger'}
															/>
															{status === 1 ? 'Active' : 'Inactive'}
														</div>
													</DropdownItem>
												))}
											</DropdownMenu>
										</Dropdown>
									</td>


									<td className='d-flex gap-3'>
										<Button
											isOutline={!darkModeStatus}
											color='dark'
											isLight={darkModeStatus}
											className={classNames('text-nowrap', {
												'border-light': !darkModeStatus,
											})}
											icon='Edit'
											onClick={() => handleUpcomingEdit(item)}>
											Edit
										</Button>
										<Button
											isOutline={!darkModeStatus}
											color='danger'
											isLight={darkModeStatus}
											className={classNames('text-nowrap', {
												'border-light': !darkModeStatus,
											})}
											icon='Delete' // Change icon to reflect delete action
											onClick={() => {
												setUpcomingEventsDeleteModal(true); // Open the modal
												setUserId(item._id); // Set the template ID for deletion
											}}
										/>
									</td>
								</tr>
							))}
						</tbody >
					</table >
				</CardBody >
				<PaginationButtons
					setCurrentPage={setCurrentPage}
					currentPage={currentPage}
					perPage={perPage}
					setPerPage={setPerPage}
					data={users}
					label="users"
					totalItems={totalData}
				/>
			</Card >

			<OffCanvas
				setOpen={setUpcomingEventsInfoOffcanvas}
				isOpen={upcomingEventsInfoOffcanvas}
				titleId='upcomingDetails'
				placement='bottom'>
				<OffCanvasHeader setOpen={setUpcomingEventsInfoOffcanvas}>
					<OffCanvasTitle id='upcomingDetails'>User Details</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					{selectedUser ? (
						<div>
							<h5>Name: {selectedUser?.fName} {selectedUser?.lName}</h5>
							<h5>Email: {selectedUser?.email}</h5>
							<h5>Status: {selectedUser?.status === 1 ? 'Active' : 'Inactive'}</h5>
							<h5>Status: {dayjs(selectedUser?.createdAt).format('MMM DD YYYY, h:mm a')}</h5>

							{/* Add any other user details you want to display */}
						</div>
					) : (
						<p>No user selected.</p>
					)}
				</OffCanvasBody>
			</OffCanvas>
			<OffCanvas
				setOpen={setUpcomingEventsEditOffcanvas}
				isOpen={upcomingEventsEditOffcanvas}
				titleId="upcomingEdit"
				isBodyScroll
				placement="end"
			>
				<OffCanvasHeader setOpen={setUpcomingEventsEditOffcanvas}>
					<OffCanvasTitle id="upcomingEdit">Change Password</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					<div className="row g-4">
						<div className="col-12">
							<FormGroup id="password" label="New Password">
								<Input
									id="password"
									name="password"
									type="password"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur} // Important for tracking touched state
									value={formik.values.password}
									isTouched={formik.touched.password}
									invalidFeedback={formik.errors.password}
								/>
							</FormGroup>
						</div>
					</div>
				</OffCanvasBody>
				<div className="row m-0">
					<div className="col-12 p-3">
						<Button
							color="info"
							className="w-100"
							onClick={() => {
								formik.handleSubmit();
							}}
						>
							Save
						</Button>
					</div>
				</div>
			</OffCanvas>
			<Modal
				setIsOpen={setUpcomingEventsAddModal}
				isOpen={upcomingEventsAddModal}
				titleId="userAdd"
				isCentered
				isScrollable
				size="lg"
			>
				<ModalHeader setIsOpen={setUpcomingEventsAddModal}>
					<h5 id="userAdd">Add User</h5>
				</ModalHeader>
				<ModalBody>
					<div className="row g-4">
						<div className="col-6">
							<FormGroup id="fName" label="First Name" isFloating>
								<Input
									placeholder="First Name"
									name="fName"
									onChange={addFormik.handleChange}
									value={addFormik.values.fName}
									onBlur={addFormik.handleBlur} // Add onBlur for touched state
									isTouched={addFormik.touched.fName}
									invalidFeedback={addFormik.errors.fName}
								/>
							</FormGroup>
						</div>
						<div className="col-6">
							<FormGroup id="lName" label="Last Name" isFloating>
								<Input
									placeholder="Last Name"
									name="lName"
									onChange={addFormik.handleChange}
									value={addFormik.values.lName}
									onBlur={addFormik.handleBlur}
									isTouched={addFormik.touched.lName}
									invalidFeedback={addFormik.errors.lName}
								/>
							</FormGroup>
						</div>
						<div className="col-12">
							<FormGroup id="email" label="Email" isFloating>
								<Input
									placeholder="Email"
									name="email"
									onChange={addFormik.handleChange}
									value={addFormik.values.email}
									type="email"
									onBlur={addFormik.handleBlur}
									isTouched={addFormik.touched.email}
									invalidFeedback={addFormik.errors.email}
								/>
							</FormGroup>
						</div>
						<div className="col-12">
							<FormGroup id="password" label="Password" isFloating>
								<Input
									placeholder="Password"
									name="password"
									onChange={addFormik.handleChange}
									value={addFormik.values.password}
									type="password"
									onBlur={addFormik.handleBlur}
									isTouched={addFormik.touched.password}
									invalidFeedback={addFormik.errors.password}
								/>
							</FormGroup>
						</div>
					</div>
				</ModalBody>
				<ModalFooter className="bg-transparent">
					<Button
						color="info"
						className="w-100"
						onClick={addFormik.handleSubmit} // Use addFormik's handleSubmit
					>
						Save
					</Button>
				</ModalFooter>
			</Modal>
			<Modal
				setIsOpen={setUpcomingEventsDeleteModal}
				isOpen={upcomingEventsDeleteModal}
				titleId="templateAdd"
				isCentered
				isScrollable
				size="lg"
			>
				<ModalHeader setIsOpen={setUpcomingEventsDeleteModal}>
					<h5 id="templateAdd">Delete User</h5>
				</ModalHeader>
				<ModalBody>
					<div className="row g-4 text-center">
						<div className="col-12">
							<SvgDeleteForever
								style={{
									color: darkModeStatus ? '#FFFFFF' : '#FF0000', // Adjust colors dynamically
									height: '50px', // Make the icon larger
									width: '50px',
								}}
							/>
						</div>
						<div className="col-12">
							<p className="mt-3">Are you sure you want to delete this user?</p>
						</div>
					</div>
				</ModalBody>
				<ModalFooter className="bg-transparent d-flex justify-content-between">
					<Button
						isOutline
						color="info"
						className="me-2 flex-grow-1"
						onClick={() => setUpcomingEventsDeleteModal(false)} // Close the modal
					>
						Cancel
					</Button>
					<Button
						color="info"
						className="ms-2 flex-grow-1"
						onClick={handleDelete} // Trigger delete action
					>
						Confirm
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default CommonUpcomingEvents;